import * as THREE from 'three'
import site from '../../../content/site.js'
import { lineTexture } from '../../Utils/localizedTexture.js'
import { t } from '../../../content/i18n.js'

/**
 * Video istasyonları: kuzeye dizilmiş dominolar → 9:16 dikey panel. Son taş paneli kuzeye devirir,
 * ekran yukarı döner ve video oynar (sessiz, döngü). Videolar yalnız tetiklenince yüklenir.
 * İçerik: site.js → reels (static/reels/<slug>.mp4 + .jpg kapak). SIFIRLA alanı istasyonu baştan kurar.
 */
const COLORS = ['accent', 'taiga', 'raspberry', 'teal', 'violet', 'blue']
const PANEL = { width: 1.8, depth: 0.12, height: 3.2 }
const SCREEN = { width: 1.66, height: 1.66 * 16 / 9 }

export default class ReelsSection
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.resources = _options.resources
        this.objects = _options.objects
        this.materials = _options.materials
        this.areas = _options.areas
        this.x = _options.x
        this.y = _options.y
        this.spacing = _options.spacing

        // Set up
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false

        this.resetTexture = lineTexture(() => t('floorReset'))
        this.stations = site.reels.map((_reel, _index) => this.addStation(_reel, this.x + _index * this.spacing, this.y))

        this.time.on('tick', () =>
        {
            for(const _station of this.stations)
            {
                this.updateStation(_station)
            }
        })
    }

    // Çalışma anında kurulan base/collision sahnesi (Objects ayrıştırıcısı: shade<Ad> mesh + cube + center)
    panelScenes()
    {
        const base = new THREE.Group()
        const body = new THREE.Mesh(new THREE.BoxGeometry(PANEL.width, PANEL.depth, PANEL.height), new THREE.MeshBasicMaterial())
        body.name = 'shadeWhite'
        body.position.z = PANEL.height / 2
        base.add(body)

        const collision = new THREE.Group()
        const cube = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1))
        cube.name = 'cube'
        cube.scale.set(PANEL.width, PANEL.depth, PANEL.height)
        cube.position.z = PANEL.height / 2
        const center = new THREE.Object3D()
        center.name = 'center'
        center.position.z = PANEL.height / 2
        collision.add(cube, center)

        return { base, collision }
    }

    addStation(_reel, _x, _y)
    {
        const station = { slug: _reel.slug, triggered: false, dominoes: [] }

        // Dominolar: güneyden kuzeye, ince yüzleri zincir yönüne dik (dünya Y ekseni)
        for(let i = 0; i < 9; i++)
        {
            station.dominoes.push(this.objects.add({
                base: this.resources.items.toysDominoBase.scene,
                collision: this.resources.items.toysDominoCollision.scene,
                offset: new THREE.Vector3(_x, _y - 0.9 - i * 0.56, 0),
                rotation: new THREE.Euler(0, 0, Math.PI * 0.5),
                duplicated: true,
                material: this.materials.items[COLORS[i % COLORS.length]],
                shadow: { sizeX: 0.9, sizeY: 0.5, offsetZ: - 0.4, alpha: 0.3 },
                mass: 0.4,
                physicsMaterial: 'domino',
                soundName: 'wood'
            }))
        }

        // Panel: ön yüzü güneye (kameraya) bakar; kuzeye devrilince ekran yukarı döner
        const scenes = this.panelScenes()
        station.panel = this.objects.add({
            base: scenes.base,
            collision: scenes.collision,
            offset: new THREE.Vector3(_x, _y, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: PANEL.width + 0.4, sizeY: 0.6, offsetZ: - 0.4, alpha: 0.35 },
            mass: 1.2,
            physicsMaterial: 'domino',
            soundName: 'plastic'
        })

        // Ekran: kapak karesi, tetiklenince video
        station.poster = new THREE.TextureLoader().load(`./reels/${_reel.slug}.jpg`)
        station.poster.colorSpace = THREE.SRGBColorSpace
        station.screenMaterial = new THREE.MeshBasicMaterial({ map: station.poster, toneMapped: false })
        station.screen = new THREE.Mesh(new THREE.PlaneGeometry(SCREEN.width, SCREEN.height), station.screenMaterial)
        station.screen.rotation.x = Math.PI * 0.5 // -Y'ye bakar
        station.screen.position.y = - PANEL.depth / 2 - 0.003
        station.panel.container.add(station.screen)

        station.video = document.createElement('video')
        station.video.muted = true
        station.video.loop = true
        station.video.playsInline = true
        station.video.preload = 'none'
        station.videoTexture = new THREE.VideoTexture(station.video)
        station.videoTexture.colorSpace = THREE.SRGBColorSpace

        // SIFIRLA alanı: istasyonun doğusunda
        station.resetArea = this.areas.add({
            position: new THREE.Vector2(_x + 3.2, _y - 3.2),
            halfExtents: new THREE.Vector2(1.3, 1.3)
        })
        station.resetArea.on('interact', () =>
        {
            this.resetStation(station)
        })
        const label = new THREE.Mesh(
            new THREE.PlaneGeometry(3.4, 0.85),
            new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, color: 0xffffff, alphaMap: this.resetTexture })
        )
        label.position.set(_x + 3.2, _y - 5.1, 0.002)
        label.matrixAutoUpdate = false
        label.updateMatrix()
        this.container.add(label)

        return station
    }

    updateStation(_station)
    {
        if(_station.triggered)
        {
            return
        }

        // Panel ~50°'den fazla eğilince (devriliyor) video başlar
        const up = new THREE.Vector3(0, 0, 1).applyQuaternion(_station.panel.container.quaternion)
        if(up.z < 0.64)
        {
            _station.triggered = true
            if(!_station.video.src)
            {
                _station.video.src = `./reels/${_station.slug}.mp4`
            }
            _station.video.currentTime = 0
            _station.video.play().catch(() => {})
            _station.screenMaterial.map = _station.videoTexture
            _station.screenMaterial.needsUpdate = true
        }
    }

    resetStation(_station)
    {
        for(const _item of [..._station.dominoes, _station.panel])
        {
            _item.collision.reset()
            _item.collision.body.velocity.set(0, 0, 0)
            _item.collision.body.angularVelocity.set(0, 0, 0)
        }

        _station.video.pause()
        _station.screenMaterial.map = _station.poster
        _station.screenMaterial.needsUpdate = true
        _station.triggered = false
    }
}
